import { useCallback, useEffect, useState } from 'react';
import {
  getPushConfig, getPushSubscriptions, registerPushSubscription, deletePushSubscription,
  PushSubscriptionRecord,
} from '../composables/notificationAPI';

// key ที่ผูก subscription บนอุปกรณ์นี้กับ record ฝั่ง backend ไว้ข้ามการ reload หน้า
// (ต่างจาก reference console เดิมที่เก็บไว้ใน memory อย่างเดียว เลยลืม id เมื่อ reload)
const DEVICE_ID_KEY = 'pushSubscriptionId';
const DEVICE_LABEL_KEY = 'pushDeviceLabel';

export type PushSupportState = 'unsupported' | 'insecure-context' | 'ready';

function base64ToBytes(value: string): Uint8Array {
  const raw = atob(value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (value.length % 4)) % 4));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

function getSupportState(): PushSupportState {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  if (!window.isSecureContext) return 'insecure-context';
  return 'ready';
}

export function useWebPush() {
  const [support] = useState<PushSupportState>(getSupportState);
  const [permission, setPermission] = useState<NotificationPermission>(
    'Notification' in window ? Notification.permission : 'denied'
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [devices, setDevices] = useState<PushSubscriptionRecord[]>([]);

  const checkBrowserSubscription = useCallback(async () => {
    if (support !== 'ready') return;
    try {
      const reg = await navigator.serviceWorker.getRegistration('/');
      const sub = await reg?.pushManager.getSubscription();
      setSubscribed(!!sub);
    } catch {
      setSubscribed(false);
    }
  }, [support]);

  useEffect(() => {
    void checkBrowserSubscription();
  }, [checkBrowserSubscription]);

  const refreshDevices = useCallback(async () => {
    try {
      const list = await getPushSubscriptions();
      setDevices(list);
      // Self-heal: ถ้า pushSubscriptionId ในเครื่องหาย/ไม่ตรงกับ record ไหนเลย (เช่นเคย register ไปแล้วแต่
      // localStorage โดนล้างหรือ sync ไม่ทัน) แต่ label ที่เก็บไว้ยังตรงกับ record บน backend อยู่ ก็ผูกกลับให้อัตโนมัติ
      const storedId = localStorage.getItem(DEVICE_ID_KEY);
      const storedLabel = localStorage.getItem(DEVICE_LABEL_KEY);
      if (storedLabel && (!storedId || !list.some((d) => d.id === storedId))) {
        const match = list.find((d) => d.device === storedLabel);
        if (match) localStorage.setItem(DEVICE_ID_KEY, match.id);
      }
      return list;
    } catch {
      return [];
    }
  }, []);

  const enable = useCallback(async () => {
    if (support === 'unsupported') throw new Error('เบราว์เซอร์นี้ไม่รองรับ Web Push');
    if (support === 'insecure-context') throw new Error('Web Push ต้องเปิดผ่าน HTTPS');

    setBusy(true);
    try {
      const config = await getPushConfig();
      if (!config.available || !config.publicKey) {
        throw new Error('เซิร์ฟเวอร์ยังไม่เปิดใช้งาน Web Push');
      }
      const granted = await Notification.requestPermission();
      setPermission(granted);
      if (granted !== 'granted') throw new Error('ไม่ได้รับอนุญาตให้แจ้งเตือน: ' + granted);

      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, reject) => setTimeout(() => reject(new Error('Service Worker ไม่พร้อม')), 12000)),
      ]);

      let sub = await reg.pushManager.getSubscription();
      const desiredKey = base64ToBytes(config.publicKey);
      if (sub?.options.applicationServerKey) {
        const existing = new Uint8Array(sub.options.applicationServerKey as ArrayBuffer);
        const mismatched = existing.length !== desiredKey.length || existing.some((b, i) => b !== desiredKey[i]);
        if (mismatched) {
          await sub.unsubscribe();
          sub = null;
        }
      }
      if (!sub) {
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: desiredKey });
      }

      const json = sub.toJSON();
      // ผูก label ไว้กับเครื่องนี้ทันทีตั้งแต่สร้าง (ไม่ใช่ค่อยเซฟตอน match เจอ) กัน enable() รอบถัดไป
      // สุ่ม label ใหม่ซ้ำ ๆ จนกลายเป็นอุปกรณ์ซ้ำซ้อนบน backend ทั้งที่เป็นเครื่องเดียวกัน
      let label = localStorage.getItem(DEVICE_LABEL_KEY);
      if (!label) {
        label = `Browser · ${crypto.randomUUID()}`;
        localStorage.setItem(DEVICE_LABEL_KEY, label);
      }
      const updated = await registerPushSubscription({
        endpoint: json.endpoint as string,
        keys: json.keys as { p256dh: string; auth: string },
        device: label,
      });
      const record = updated.find((d) => d.device === label);
      if (record) localStorage.setItem(DEVICE_ID_KEY, record.id);
      setSubscribed(true);
      setDevices(updated);
      return updated;
    } finally {
      setBusy(false);
    }
  }, [support]);

  const disable = useCallback(async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/');
      const sub = await reg?.pushManager.getSubscription();
      const knownId = localStorage.getItem(DEVICE_ID_KEY);
      if (knownId) {
        try { await deletePushSubscription(knownId); } catch { /* record อาจถูกลบไปแล้วจากอีกฝั่ง */ }
      }
      if (sub) await sub.unsubscribe();
      localStorage.removeItem(DEVICE_ID_KEY);
      localStorage.removeItem(DEVICE_LABEL_KEY);
      setSubscribed(false);
      await refreshDevices();
    } finally {
      setBusy(false);
    }
  }, [refreshDevices]);

  // เคลียร์ subscription ฝั่งเบราว์เซอร์ + localStorage เมื่ออุปกรณ์ที่ถูกลบคืออุปกรณ์นี้เอง (กันหลุด sync กับ backend)
  const unsubscribeThisBrowser = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration('/');
      const sub = await reg?.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
    } catch {
      // ไม่มี registration/subscription อยู่แล้วก็ข้ามได้
    }
    localStorage.removeItem(DEVICE_ID_KEY);
    localStorage.removeItem(DEVICE_LABEL_KEY);
    setSubscribed(false);
  }, []);

  const removeDevice = useCallback(async (id: string) => {
    await deletePushSubscription(id);
    if (localStorage.getItem(DEVICE_ID_KEY) === id) await unsubscribeThisBrowser();
    await refreshDevices();
  }, [refreshDevices, unsubscribeThisBrowser]);

  // ลบหลายอุปกรณ์พร้อมกัน (เช่น จากหน้าตั้งค่าที่ติ๊กเลือกได้หลายอัน) แล้ว refresh ทีเดียวตอนจบ
  const removeDevices = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return;
    const currentId = localStorage.getItem(DEVICE_ID_KEY);
    await Promise.all(ids.map((id) => deletePushSubscription(id)));
    if (currentId && ids.includes(currentId)) await unsubscribeThisBrowser();
    await refreshDevices();
  }, [refreshDevices, unsubscribeThisBrowser]);

  return {
    support, permission, subscribed, busy, devices,
    currentDeviceId: localStorage.getItem(DEVICE_ID_KEY),
    enable, disable, refreshDevices, removeDevice, removeDevices,
  };
}
